import HuskyTooltip from "@husky-hrc/Components/FormFields/HuskyTooltip";
import InputSubText from "@husky-hrc/Components/FormFields/InputSubText";
import RevertButton from "@husky-hrc/Components/FormFields/Revert";
import { InputModel, InputType } from "@husky-hrc/Framework/Types/ApiModel";
import { inputStyles, labelStyles } from "@husky-hrc/Utilities/Mixins/form";
import { ProductConfigViewContext } from "@husky-hrc/Widgets/ProductConfigView";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden/VisuallyHidden";
import React from "react";
import styled, { css } from "styled-components";

export interface TextFieldStyles {
    textFieldContainerStyles?: InjectableCss;
    labelContainerStyles?: InjectableCss;
}

export const textFieldStyles: TextFieldStyles = {
    textFieldContainerStyles: {
        css: css`
            position: relative;
            width: 100%;
            > p {
                line-height: 28px;
                font-size: 16px;
            }
            label {
                position: relative;
            }
        `,
    },
    labelContainerStyles: {
        css: css`
            ${labelStyles}
            display: flex;
            align-items: center;
        `,
    },
};

const Input = styled.input<{ hasError: boolean }>`
    width: 100%;
    margin-top: 10px;
    ${props => (props.hasError ? inputStyles(true) : inputStyles(false))};
`;

const Units = styled.div`
    position: absolute;
    top: 0;
    right: 30px;
    color: #95989c;
    ${Input}:hover ~ &, ${Input}:focus ~ &, ${Input}[data-committed="true"] ~ & {
        color: ${getColor("text.main")};
    }
    ${Input}:disabled ~ & {
        color: ${getColor("text.disabled")};
    }
`;

interface OwnProps {
    input: InputModel;
    onChange: (parameter: string, value: string) => void;
    onUncommit: (parameter: string) => void;
}

type Props = OwnProps;

const styles = textFieldStyles;

const TextField = ({ input, onChange, onUncommit }: Props) => {
    const [value, setValue] = React.useState(input.value);
    const [errorMessage, setErrorMessage] = React.useState("");
    const productConfigViewContext = React.useContext(ProductConfigViewContext);

    React.useEffect(() => {
        setValue(input.value);
    }, [input]);

    React.useEffect(() => {
        validate();
        productConfigViewContext.validators[input.name] = validate;
        return () => {
            delete productConfigViewContext.validators[input.name];
        };
    }, [value]);

    const validate = () => {
        if (input.required && !value) {
            setErrorMessage(translate("This field is required"));
            return false;
        }
        if (Number(value) < input.min || Number(value) > input.max) {
            setErrorMessage(
                translate("You must enter a value within {0} and {1}")
                    .replace("{0}", `${input.min}`)
                    .replace("{1}", `${input.max}`),
            );
            return false;
        }
        if (input.type === InputType.TEXT && (value?.length ?? 0) > 2000) {
            setErrorMessage(translate("You must enter a value with at most {0} characters").replace("{0}", "2000"));
            return false;
        }
        setErrorMessage("");
        return true;
    };

    const handleInputChange = () => {
        const isValid = validate();
        if (!isValid) {
            return;
        }
        const parameter = input.name;
        const defaultValue = input.defaultValue.description;

        if ((value !== defaultValue || !input.committed) && parameter && value) {
            onChange(parameter, value);
        }
    };

    return (
        <StyledWrapper {...styles.textFieldContainerStyles}>
            <StyledWrapper {...styles.labelContainerStyles}>
                <span id={`${input.name}_label`} data-required={input.required}>
                    {input.description}
                </span>
                {input.tooltip && <HuskyTooltip input={input} />}
                {input.committed && <RevertButton input={input} onUncommit={onUncommit} />}
            </StyledWrapper>
            <p id={`${input.name}_tcInfoText`}>{input.tcInfoText}</p>

            <label>
                <Input
                    type={input.type}
                    onBlur={handleInputChange}
                    name={input.name}
                    disabled={input.disabled}
                    data-committed={input.committed}
                    value={value}
                    // eslint-disable-next-line no-restricted-globals
                    min={isNaN(input.min) ? undefined : input.min}
                    // eslint-disable-next-line no-restricted-globals
                    max={isNaN(input.max) ? undefined : input.max}
                    required={input.required}
                    placeholder={input.placeholder}
                    hasError={!!errorMessage}
                    onChange={e => setValue(e.target.value)}
                    aria-labelledby={`${input.name}_label`}
                    aria-describedby={`
                        ${input.name}_tcInfoText${input.units ? ` ${input.name}_units` : ""} ${input.name}_subText`}
                    aria-invalid={!!errorMessage}
                    aria-required={input.required}
                />
                {input.units && (
                    <Units id={`${input.name}_units`}>
                        <VisuallyHidden>Units:</VisuallyHidden>
                        {input.units}
                    </Units>
                )}
            </label>
            <InputSubText id={`${input.name}_subText`} input={input} errorMessage={errorMessage} />
        </StyledWrapper>
    );
};

export default TextField;
